import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Collapse,
    Form,
    InputGroup,
    Row,
    Spinner,
    Table,
    Toast,
    ToastContainer,
} from 'react-bootstrap';
import axios from 'axios';
import TableFooter from '../components/table/footer';

const headers = {
    Accept: 'application/json',
    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
    Authorization: 'Bearer ' + document.querySelector('meta[name="api-token"]')?.getAttribute('content'),
};

const formatDate = (value) => {
    if (!value) {
        return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleString();
};

const endpointStatusBadge = (endpoint) => {
    if (endpoint.online === true || endpoint.online === 1) {
        return <Badge bg="success">Online</Badge>;
    }

    if (endpoint.online === false || endpoint.online === 0) {
        return <Badge bg="secondary">Offline</Badge>;
    }

    return <Badge bg="light" text="dark" className="border">Onbekend</Badge>;
};

const managedBadge = (managed) => {
    if (managed === true || managed === 1) {
        return <Badge bg="primary">Managed</Badge>;
    }

    if (managed === false || managed === 0) {
        return <Badge bg="warning">Unmanaged</Badge>;
    }

    return <Badge bg="light" text="dark" className="border">Onbekend</Badge>;
};

const outdatedBadge = (outdated) => {
    if (outdated === true || outdated === 1) {
        return <Badge bg="warning">Update nodig</Badge>;
    }

    if (outdated === false || outdated === 0) {
        return <Badge bg="success">Up-to-date</Badge>;
    }

    return null;
};

const PowerIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2v10" />
        <path d="M18.4 6.6a9 9 0 1 1-12.8 0" />
    </svg>
);

const ListIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 6h13" />
        <path d="M8 12h13" />
        <path d="M8 18h13" />
        <path d="M3 6h.01" />
        <path d="M3 12h.01" />
        <path d="M3 18h.01" />
    </svg>
);

const TrashIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="M19 6l-1 14H6L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
    </svg>
);

const InfoIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
    </svg>
);

const getQuarantineItemId = (item) => {
    return item.id
        || item.quarantineItemId
        || item.quarantine_item_id
        || item.itemId
        || item.item_id
        || null;
};

const EndpointsComponent = () => {
    const [endpoints, setEndpoints] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [filters, setFilters] = useState({
        company: '',
        status: '',
        managed: '',
        outdated: '',
        operating_system: '',
        policy: '',
    });

    const [filterOptions, setFilterOptions] = useState({
        companies: [],
        operating_systems: [],
        policies: [],
    });

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalItems, setTotalItems] = useState(0);

    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState('');

    const [openEndpointId, setOpenEndpointId] = useState(null);
    const [endpointDetailsById, setEndpointDetailsById] = useState({});
    const [quarantineItemsByEndpointId, setQuarantineItemsByEndpointId] = useState({});
    const [actionLoadingKey, setActionLoadingKey] = useState(null);

    const [toast, setToast] = useState({
        show: false,
        variant: 'success',
        title: '',
        message: '',
    });

    const showToast = ({ variant = 'success', title = '', message = '' }) => {
        setToast({
            show: true,
            variant,
            title,
            message,
        });
    };

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 350);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [search]);

    const loadFilterOptions = async () => {
        try {
            const response = await axios.get('/api/v1/endpoints/filter-options', {
                headers,
            });

            setFilterOptions({
                companies: response.data?.companies || [],
                operating_systems: response.data?.operating_systems || [],
                policies: response.data?.policies || [],
            });
        } catch (error) {
            console.error(error);

            showToast({
                variant: 'danger',
                title: 'Filters laden mislukt',
                message: 'De filteropties konden niet worden opgehaald.',
            });
        }
    };

    const loadEndpoints = async ({ initialLoad = false } = {}) => {
        if (initialLoad) {
            setLoading(true);
        } else {
            setTableLoading(true);
        }

        setError('');

        try {
            const response = await axios.get('/api/v1/endpoints', {
                headers,
                params: {
                    page,
                    page_size: pageSize,
                    search: debouncedSearch,
                    company: filters.company,
                    status: filters.status,
                    managed: filters.managed,
                    outdated: filters.outdated,
                    operating_system: filters.operating_system,
                    policy: filters.policy,
                },
            });

            setEndpoints(response.data?.data || []);
            setTotalItems(Number(response.data?.total || 0));
        } catch (error) {
            console.error(error);

            setEndpoints([]);
            setTotalItems(0);
            setError(error.response?.data?.message || 'Endpoints konden niet worden opgehaald.');
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    useEffect(() => {
        loadFilterOptions();
        loadEndpoints({ initialLoad: true });
    }, []);

    useEffect(() => {
        loadEndpoints();
    }, [page, pageSize, debouncedSearch, filters]);

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const updateFilter = (key, value) => {
        setFilters((current) => ({
            ...current,
            [key]: value,
        }));

        setPage(1);
    };

    const resetFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setFilters({
            company: '',
            status: '',
            managed: '',
            outdated: '',
            operating_system: '',
            policy: '',
        });
        setPage(1);
    };

    const resultLabel = useMemo(() => {
        if (totalItems === 0) {
            return 'Geen endpoints gevonden';
        }

        return `${totalItems} endpoint${totalItems === 1 ? '' : 's'} gevonden`;
    }, [totalItems]);

    const loadEndpointDetails = async (endpoint) => {
        if (!endpoint.id) {
            return;
        }

        if (endpointDetailsById[endpoint.id]) {
            return;
        }

        setActionLoadingKey(`${endpoint.id}:details`);

        try {
            const response = await axios.get(`/api/v1/endpoints/${endpoint.id}`, {
                headers,
            });

            setEndpointDetailsById((current) => ({
                ...current,
                [endpoint.id]: response.data?.data || endpoint,
            }));
        } catch (error) {
            console.error(error);

            showToast({
                variant: 'danger',
                title: 'Details ophalen mislukt',
                message: error.response?.data?.message || 'Endpoint details konden niet worden opgehaald.',
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const toggleDetails = async (endpoint) => {
        const nextOpenId = openEndpointId === endpoint.id ? null : endpoint.id;

        setOpenEndpointId(nextOpenId);

        if (nextOpenId) {
            await loadEndpointDetails(endpoint);
        }
    };

    const handleUninstallEndpoint = async (endpoint) => {
        if (!endpoint.id) {
            return;
        }

        const confirmed = window.confirm(
            `Weet je zeker dat je een uninstall taak wilt aanmaken voor ${endpoint.name || endpoint.id}?`
        );

        if (!confirmed) {
            return;
        }

        setActionLoadingKey(`${endpoint.id}:uninstall`);

        try {
            const response = await axios.post(
                `/api/v1/endpoints/${endpoint.id}/uninstall`,
                {},
                { headers }
            );

            showToast({
                variant: 'success',
                title: 'Uninstall taak aangemaakt',
                message: response.data?.message || 'De uninstall opdracht is verzonden.',
            });
        } catch (error) {
            console.error(error);

            showToast({
                variant: 'danger',
                title: 'Uninstall mislukt',
                message: error.response?.data?.message || 'Uninstall taak kon niet worden aangemaakt.',
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const handleGetQuarantineItems = async (endpoint) => {
        if (!endpoint.id || !endpoint.company_id) {
            showToast({
                variant: 'danger',
                title: 'Gegevens ontbreken',
                message: 'Endpoint ID of company ID ontbreekt.',
            });
            return;
        }

        setActionLoadingKey(`${endpoint.id}:quarantine-list`);

        try {
            const response = await axios.get(
                `/api/v1/endpoints/${endpoint.id}/quarantine-items`,
                {
                    headers,
                    params: {
                        company_id: endpoint.company_id,
                    },
                }
            );

            const items = response.data?.items || [];

            setQuarantineItemsByEndpointId((current) => ({
                ...current,
                [endpoint.id]: items,
            }));

            showToast({
                variant: 'success',
                title: 'Quarantaine opgehaald',
                message: `${items.length} quarantaine item(s) gevonden.`,
            });

            setOpenEndpointId(endpoint.id);
        } catch (error) {
            console.error(error);

            showToast({
                variant: 'danger',
                title: 'Quarantaine ophalen mislukt',
                message: error.response?.data?.message || 'Quarantaine items konden niet worden opgehaald.',
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const handleRemoveQuarantineItems = async (endpoint) => {
        const items = quarantineItemsByEndpointId[endpoint.id] || [];
        const quarantineItemIds = items
            .map((item) => getQuarantineItemId(item))
            .filter(Boolean);

        if (quarantineItemIds.length === 0) {
            showToast({
                variant: 'danger',
                title: 'Geen items',
                message: 'Haal eerst de quarantaine items op.',
            });
            return;
        }

        const confirmed = window.confirm(
            `Weet je zeker dat je ${quarantineItemIds.length} quarantaine item(s) wilt verwijderen?`
        );

        if (!confirmed) {
            return;
        }

        setActionLoadingKey(`${endpoint.id}:quarantine-remove`);

        try {
            const response = await axios.post(
                `/api/v1/endpoints/${endpoint.id}/remove-quarantine-items`,
                {
                    quarantine_item_ids: quarantineItemIds,
                },
                { headers }
            );

            showToast({
                variant: 'success',
                title: 'Verwijdertaak aangemaakt',
                message: response.data?.message || 'De verwijderopdracht is verzonden.',
            });
        } catch (error) {
            console.error(error);

            showToast({
                variant: 'danger',
                title: 'Verwijderen mislukt',
                message: error.response?.data?.message || 'Verwijdertaak kon niet worden aangemaakt.',
            });
        } finally {
            setActionLoadingKey(null);
        }
    };

    const renderDetailItem = (label, value, code = false) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return (
            <div className="mb-2">
                <div className="text-muted small">{label}</div>
                {code ? (
                    <code className="d-block small text-break bg-body-tertiary border rounded px-2 py-1">
                        {String(value)}
                    </code>
                ) : (
                    <div className="fw-semibold text-break">{String(value)}</div>
                )}
            </div>
        );
    };

    const renderEndpointDetails = (endpoint) => {
        const details = endpointDetailsById[endpoint.id] || endpoint;
        const quarantineItems = quarantineItemsByEndpointId[endpoint.id] || [];
        const raw = details.raw || endpoint.raw || details;

        return (
            <div className="p-3 bg-body-tertiary border-top">
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                    <div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <h6 className="mb-0">{details.name || endpoint.name}</h6>
                            {endpointStatusBadge(details)}
                            {managedBadge(details.managed)}
                            {outdatedBadge(details.product_outdated)}
                        </div>

                        <div className="text-muted small">
                            {details.company_name || '-'} · {details.ip || '-'}
                        </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            className="d-inline-flex align-items-center gap-2"
                            disabled={actionLoadingKey === `${endpoint.id}:quarantine-list`}
                            onClick={() => handleGetQuarantineItems(endpoint)}
                        >
                            {actionLoadingKey === `${endpoint.id}:quarantine-list`
                                ? <Spinner animation="border" size="sm" />
                                : <ListIcon />}
                            <span>Quarantaine items</span>
                        </Button>

                        <Button
                            type="button"
                            size="sm"
                            variant="outline-danger"
                            className="d-inline-flex align-items-center gap-2"
                            disabled={actionLoadingKey === `${endpoint.id}:quarantine-remove` || quarantineItems.length === 0}
                            onClick={() => handleRemoveQuarantineItems(endpoint)}
                        >
                            {actionLoadingKey === `${endpoint.id}:quarantine-remove`
                                ? <Spinner animation="border" size="sm" />
                                : <TrashIcon />}
                            <span>Verwijder items ({quarantineItems.length})</span>
                        </Button>

                        <Button
                            type="button"
                            size="sm"
                            variant="outline-warning"
                            className="d-inline-flex align-items-center gap-2"
                            disabled={actionLoadingKey === `${endpoint.id}:uninstall`}
                            onClick={() => handleUninstallEndpoint(endpoint)}
                        >
                            {actionLoadingKey === `${endpoint.id}:uninstall`
                                ? <Spinner animation="border" size="sm" />
                                : <PowerIcon />}
                            <span>Uninstall</span>
                        </Button>
                    </div>
                </div>

                <Row className="g-3 mb-3">
                    <Col lg={4}>
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Endpoint</div>
                            {renderDetailItem('Naam', details.name)}
                            {renderDetailItem('Label', details.label)}
                            {renderDetailItem('FQDN', details.fqdn)}
                            {renderDetailItem('IP-adres', details.ip)}
                            {renderDetailItem('Endpoint ID', details.id, true)}
                        </div>
                    </Col>

                    <Col lg={4}>
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Bedrijf / locatie</div>
                            {renderDetailItem('Bedrijf', details.company_name)}
                            {renderDetailItem('Company ID', details.company_id, true)}
                            {renderDetailItem('Groep ID', details.group_id, true)}
                            {renderDetailItem('Pad', Array.isArray(details.inventory_path) ? details.inventory_path.join(' / ') : '')}
                        </div>
                    </Col>

                    <Col lg={4}>
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Beveiliging</div>
                            {renderDetailItem('Managed', details.managed === true || details.managed === 1 ? 'Ja' : 'Nee')}
                            {renderDetailItem('Managed with BEST', details.managed_with_best === true || details.managed_with_best === 1 ? 'Ja' : 'Nee')}
                            {renderDetailItem('Product verouderd', details.product_outdated === true || details.product_outdated === 1 ? 'Ja' : 'Nee')}
                            {renderDetailItem('Policy', details.policy_name)}
                            {renderDetailItem('Laatste scan', details.last_scan_name)}
                            {renderDetailItem('Laatst gezien', formatDate(details.last_seen))}
                            {renderDetailItem('Laatst gesynchroniseerd', formatDate(details.synced_at))}
                        </div>
                    </Col>
                </Row>

                <Row className="g-3 mb-3">
                    <Col lg={6}>
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="fw-semibold mb-2">Systeem</div>
                            {renderDetailItem('Besturingssysteem', details.operating_system)}
                            {renderDetailItem('Machine type', details.machine_type)}
                            {renderDetailItem('MAC-adressen', Array.isArray(details.macs) ? details.macs.join(', ') : '')}
                        </div>
                    </Col>

                    <Col lg={6}>
                        <div className="border rounded bg-body p-3 h-100">
                            <div className="d-flex justify-content-between gap-2 mb-2">
                                <div className="fw-semibold">Quarantaine</div>
                                <Badge bg="light" text="dark" className="border">
                                    {quarantineItems.length} item(s)
                                </Badge>
                            </div>

                            {quarantineItems.length === 0 && (
                                <div className="text-muted small">
                                    Nog geen quarantaine items opgehaald of geen items gevonden.
                                </div>
                            )}

                            {quarantineItems.length > 0 && (
                                <div className="table-responsive">
                                    <Table size="sm" hover className="mb-0">
                                        <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Pad</th>
                                            <th>Datum</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {quarantineItems.map((item, index) => (
                                            <tr key={getQuarantineItemId(item) || index}>
                                                <td>
                                                    <code className="small">
                                                        {getQuarantineItemId(item) || '-'}
                                                    </code>
                                                </td>
                                                <td className="small text-break">
                                                    {item.filePath || item.path || item.name || '-'}
                                                </td>
                                                <td className="small">
                                                    {formatDate(item.date || item.createdAt || item.quarantineDate)}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <details>
                    <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                        Toon ruwe JSON
                    </summary>

                    <pre
                        className="m-0 mt-2 font-monospace bg-body border rounded p-3"
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 360,
                            overflow: 'auto',
                            fontSize: '0.78rem',
                        }}
                    >
                        {JSON.stringify(raw, null, 2)}
                    </pre>
                </details>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" />
                Endpoints laden...
            </div>
        );
    }

    return (
        <>
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1080 }}>
                <Toast
                    bg={toast.variant}
                    show={toast.show}
                    onClose={() => setToast((current) => ({ ...current, show: false }))}
                    delay={4500}
                    autohide
                >
                    <Toast.Header>
                        <strong className="me-auto">{toast.title || 'Melding'}</strong>
                    </Toast.Header>

                    <Toast.Body className={toast.variant === 'danger' || toast.variant === 'success' ? 'text-white' : ''}>
                        {toast.message}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                        <div>
                            <h5 className="mb-1">Endpoints</h5>
                            <div className="text-muted">
                                Overzicht van endpoints uit Bitdefender GravityZone.
                            </div>
                        </div>

                        <Button
                            type="button"
                            variant="outline-primary"
                            onClick={() => {
                                loadFilterOptions();
                                loadEndpoints();
                            }}
                            disabled={tableLoading}
                        >
                            {tableLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Laden...
                                </>
                            ) : (
                                'Vernieuwen'
                            )}
                        </Button>
                    </div>

                    <Row className="g-3">
                        <Col lg={4}>
                            <Form.Group controlId="endpoint-search">
                                <Form.Label>Zoeken</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="search"
                                        placeholder="Zoek op naam, IP-adres, FQDN..."
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                    />

                                    {search && (
                                        <Button
                                            type="button"
                                            variant="outline-secondary"
                                            onClick={() => setSearch('')}
                                        >
                                            Wissen
                                        </Button>
                                    )}
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={4}>
                            <Form.Group controlId="endpoint-company-filter">
                                <Form.Label>Bedrijf</Form.Label>
                                <Form.Select
                                    value={filters.company}
                                    onChange={(event) => updateFilter('company', event.target.value)}
                                >
                                    <option value="">Alle bedrijven</option>
                                    {filterOptions.companies.map((company) => (
                                        <option key={company} value={company}>
                                            {company}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={4}>
                            <Form.Group controlId="endpoint-os-filter">
                                <Form.Label>Windows / OS versie</Form.Label>
                                <Form.Select
                                    value={filters.operating_system}
                                    onChange={(event) => updateFilter('operating_system', event.target.value)}
                                >
                                    <option value="">Alle versies</option>
                                    {filterOptions.operating_systems.map((os) => (
                                        <option key={os} value={os}>
                                            {os}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={3}>
                            <Form.Group controlId="endpoint-status-filter">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={filters.status}
                                    onChange={(event) => updateFilter('status', event.target.value)}
                                >
                                    <option value="">Alle statussen</option>
                                    <option value="online">Online</option>
                                    <option value="offline">Offline</option>
                                    <option value="unknown">Onbekend</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={3}>
                            <Form.Group controlId="endpoint-managed-filter">
                                <Form.Label>Beheer</Form.Label>
                                <Form.Select
                                    value={filters.managed}
                                    onChange={(event) => updateFilter('managed', event.target.value)}
                                >
                                    <option value="">Alles</option>
                                    <option value="managed">Managed</option>
                                    <option value="unmanaged">Unmanaged</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={3}>
                            <Form.Group controlId="endpoint-outdated-filter">
                                <Form.Label>Updates</Form.Label>
                                <Form.Select
                                    value={filters.outdated}
                                    onChange={(event) => updateFilter('outdated', event.target.value)}
                                >
                                    <option value="">Alles</option>
                                    <option value="outdated">Update nodig</option>
                                    <option value="current">Up-to-date</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={3}>
                            <Form.Group controlId="endpoint-policy-filter">
                                <Form.Label>Policy</Form.Label>
                                <Form.Select
                                    value={filters.policy}
                                    onChange={(event) => updateFilter('policy', event.target.value)}
                                >
                                    <option value="">Alle policies</option>
                                    {filterOptions.policies.map((policy) => (
                                        <option key={policy} value={policy}>
                                            {policy}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col xs={12} className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <div className="text-muted small">{resultLabel}</div>

                            <Button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                onClick={resetFilters}
                            >
                                Filters wissen
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger">
                    {error}
                </Alert>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="position-relative">
                        {tableLoading && (
                            <div
                                className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-start justify-content-center pt-5 bg-body bg-opacity-75"
                                style={{ zIndex: 5 }}
                            >
                                <div className="d-inline-flex align-items-center gap-2 text-muted bg-body border rounded px-3 py-2 shadow-sm">
                                    <Spinner animation="border" size="sm" />
                                    Endpoints laden...
                                </div>
                            </div>
                        )}

                        <div className="table-responsive">
                            <Table hover bordered className="align-middle mb-0">
                                <thead>
                                <tr>
                                    <th>Endpoint</th>
                                    <th>Bedrijf</th>
                                    <th>IP-adres</th>
                                    <th>Status</th>
                                    <th>Beheer</th>
                                    <th>Product / OS</th>
                                    <th>Laatst gezien</th>
                                    <th>Acties</th>
                                </tr>
                                </thead>

                                <tbody>
                                {endpoints.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center text-muted py-4">
                                            Geen endpoints gevonden.
                                        </td>
                                    </tr>
                                )}

                                {endpoints.map((endpoint) => (
                                    <>
                                        <tr key={endpoint.id || `${endpoint.name}-${endpoint.ip}`}>
                                            <td>
                                                <div className="fw-semibold">
                                                    {endpoint.name || '-'}
                                                </div>

                                                {endpoint.fqdn && (
                                                    <div className="text-muted small">
                                                        {endpoint.fqdn}
                                                    </div>
                                                )}

                                                {endpoint.id && (
                                                    <code className="small text-break">
                                                        {endpoint.id}
                                                    </code>
                                                )}
                                            </td>

                                            <td>
                                                <div className="fw-semibold">
                                                    {endpoint.company_name || '-'}
                                                </div>

                                                {endpoint.company_id && (
                                                    <div className="text-muted small text-break">
                                                        {endpoint.company_id}
                                                    </div>
                                                )}
                                            </td>

                                            <td>{endpoint.ip || '-'}</td>

                                            <td>{endpointStatusBadge(endpoint)}</td>

                                            <td>{managedBadge(endpoint.managed)}</td>

                                            <td>
                                                <div>{endpoint.product_installed || '-'}</div>

                                                {endpoint.operating_system && (
                                                    <div className="text-muted small">
                                                        {endpoint.operating_system}
                                                    </div>
                                                )}

                                                {outdatedBadge(endpoint.product_outdated) && (
                                                    <div className="mt-1">
                                                        {outdatedBadge(endpoint.product_outdated)}
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                {endpoint.last_seen ? (
                                                    formatDate(endpoint.last_seen)
                                                ) : (
                                                    <span className="text-muted">
                                                        -
                                                    </span>
                                                )}

                                                {!endpoint.last_seen && endpoint.synced_at && (
                                                    <div className="text-muted small">
                                                        Sync: {formatDate(endpoint.synced_at)}
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                <div className="d-flex flex-wrap gap-2">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline-primary"
                                                        className="d-inline-flex align-items-center gap-1"
                                                        onClick={() => toggleDetails(endpoint)}
                                                    >
                                                        {actionLoadingKey === `${endpoint.id}:details`
                                                            ? <Spinner animation="border" size="sm" />
                                                            : <InfoIcon />}
                                                        <span>
                                                            {openEndpointId === endpoint.id ? 'Verberg' : 'Details'}
                                                        </span>
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline-primary"
                                                        title="Quarantaine items ophalen"
                                                        disabled={actionLoadingKey === `${endpoint.id}:quarantine-list`}
                                                        onClick={() => handleGetQuarantineItems(endpoint)}
                                                    >
                                                        {actionLoadingKey === `${endpoint.id}:quarantine-list`
                                                            ? <Spinner animation="border" size="sm" />
                                                            : <ListIcon />}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>

                                        <tr className={openEndpointId === endpoint.id ? '' : 'd-none'}>
                                            <td colSpan={8} className="p-0">
                                                <Collapse in={openEndpointId === endpoint.id}>
                                                    <div>
                                                        {renderEndpointDetails(endpoint)}
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <TableFooter
                totalItems={totalItems}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={tableLoading}
                className="mt-2"
            />
        </>
    );
};

export default EndpointsComponent;
